import React, { useEffect, useState } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { Icons } from '../../../shared/icons/Icons';

interface HelpDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  brand: BrandConfig;
}

interface FaqItem {
  question: string;
  answer: string;
}

const FAQ_ITEMS: FaqItem[] = [
  {
    question: 'How long does the court take to accept a filing?',
    answer: 'Most filings are reviewed within 1-2 business days. You will receive a confirmation email once your filing has been accepted or if any corrections are needed.',
  },
  {
    question: "What if my document isn't text-searchable?",
    answer: 'Documents must be text-searchable PDFs. If yours is image-only, we will run OCR on upload to make it searchable before submission. You can also re-save it as a searchable PDF from your word processor.',
  },
  {
    question: 'Can I file on weekends?',
    answer: 'You can submit filings 24/7, including weekends and holidays. The court only reviews submissions during business hours, so weekend filings are queued for review the next business day.',
  },
];

const RESOURCE_LINKS = [
  { label: 'eFiling Walkthrough', icon: 'Globe' as const, href: '#walkthrough' },
  { label: 'Filing Guide', icon: 'Document' as const, href: '#guide' },
  { label: 'Chat with Support', icon: 'Chat' as const, href: '#chat' },
];

const HelpDrawer: React.FC<HelpDrawerProps> = ({ isOpen, onClose, brand }) => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
        data-testid="help-drawer-backdrop"
        aria-hidden="true"
      />
      <div
        className="fixed inset-y-0 right-0 max-w-sm w-full bg-white shadow-xl z-50 flex flex-col transform transition-transform duration-300 ease-in-out"
        style={{ transform: 'translateX(0)' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-drawer-title"
      >
        <div
          className="flex items-center justify-between px-6 py-4 border-b shrink-0"
          style={{ borderColor: brand.colors.border }}
        >
          <h2 id="help-drawer-title" className="text-lg font-semibold" style={{ color: brand.colors.text }}>
            Help &amp; Resources
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close drawer"
            data-testid="help-drawer-close"
          >
            <Icons.X className="w-5 h-5" style={{ color: brand.colors.textLight }} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="mb-6">
            <h3 className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: brand.colors.textLight }}>
              Resources
            </h3>
            <div className="space-y-2">
              {RESOURCE_LINKS.map((link) => {
                const Icon = Icons[link.icon];
                return (
                  <a
                    key={link.label}
                    href={link.href}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors hover:bg-gray-50"
                    style={{ borderColor: brand.colors.border, color: brand.colors.text }}
                  >
                    <Icon className="w-5 h-5" style={{ color: brand.colors.link }} />
                    <span className="text-sm font-medium">{link.label}</span>
                  </a>
                );
              })}
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: brand.colors.textLight }}>
              Frequently Asked Questions
            </h3>
            <div className="space-y-1">
              {FAQ_ITEMS.map((faq, index) => {
                const isExpanded = openFaqIndex === index;
                return (
                  <div key={faq.question} className="border rounded-lg" style={{ borderColor: brand.colors.border }}>
                    <button
                      onClick={() => setOpenFaqIndex(isExpanded ? null : index)}
                      className="w-full flex items-center justify-between px-3 py-2.5 text-left text-sm font-medium transition-colors hover:bg-gray-50"
                      style={{ color: brand.colors.text }}
                      aria-expanded={isExpanded}
                      data-testid={`faq-toggle-${index}`}
                    >
                      <span>{faq.question}</span>
                      <Icons.ChevronDown
                        className="w-4 h-4 shrink-0 ml-2 transition-transform"
                        style={{
                          color: brand.colors.textLight,
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                      />
                    </button>
                    {isExpanded && (
                      <div className="px-3 pb-3 text-sm" style={{ color: brand.colors.textLight }} data-testid={`faq-answer-${index}`}>
                        {faq.answer}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HelpDrawer;
